import React, { useEffect, useState } from 'react';
import { Shell } from '../shell/Shell';
import { Badge, ConfirmDialog, PageHeader, fmtDateTime, I, PencilIcon } from '../atoms';
import { CaseCostBreakdown, CaseCostKpis } from '../components/CaseCosts';
import { parseUsageLines, summariseCosts } from '../api/caseCosts';
import { getUserEmail } from '../auth';
import { useToasts, Toasts } from '../Toasts';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { useInfiniteScroll } from '../useInfiniteScroll';
import { errMessage, fmtCost, fmtTokens, renderMarkdown, runEvent, STALE_MESSAGE, useSettledReload } from '../pageUtils';
import {
  listPlanningCases, getPlanningCase, emitDeletePlanningCase,
  emitLinkPolicyInput, emitLinkDocument, type PlanningCase, type PlanningCaseDetail,
} from '../api/planningCases';
import {
  getPlanningAssessment, emitRequestPlanningAssessment, type PlanningAssessment,
} from '../api/planningAssessments';
import {
  listPlanningDocuments, getPlanningDocumentUploadUrl, getPlanningDocumentDownloadUrl,
  uploadPlanningDocumentToS3, emitCreatePlanningDocument, type PlanningDocument,
} from '../api/planningDocuments';
import { SiteMapPanel } from '../components/SiteMapView';
import { AssessmentHistory } from '../components/AssessmentHistory';
import LocateStep from './LocateStep';
import { LOCATE_ENABLED, parseSavedLocation } from '../api/locate';
import { SiteLocationNotes } from '../components/SiteLocationNotes';
import { SiteFeaturesCard, AssessInputCard } from '../components/SiteBreakdown';
import { resolveUserIdentities, type UserIdentity } from '../api/users';
import { getPolicyInput, getPolicyInputDownloadUrl, type PolicyInput } from '../api/policyInputs';
import { DOCUMENT_CATEGORIES, LOCATION_PLAN_CATEGORY, STATEMENT_CATEGORY } from '../documentCategories';

type AddToast = (type: 'success' | 'error' | 'info' | 'warn', message: string, persistent?: boolean) => string;
function parseSiteMap(siteLocation: string | null | undefined): boolean {
  try { return !!siteLocation && typeof (JSON.parse(siteLocation) as { lat?: unknown }).lat === 'number'; } catch { return false; }
}

interface LocateUsage { inputTokens: number; outputTokens: number; estimatedCostUsd: number; osMapTiles: number; osMapCostGbp: number }

function parseLocateUsage(siteLocation: string | null | undefined): LocateUsage | null {
  if (!siteLocation) return null;
  try {
    const u = (JSON.parse(siteLocation) as { usage?: Partial<LocateUsage> }).usage;
    if (!u || typeof u.estimatedCostUsd !== 'number') return null;
    return {
      inputTokens: u.inputTokens ?? 0, outputTokens: u.outputTokens ?? 0, estimatedCostUsd: u.estimatedCostUsd,
      osMapTiles: u.osMapTiles ?? 0, osMapCostGbp: u.osMapCostGbp ?? 0,
    };
  } catch {
    return null;
  }
}

type DetailTab = 'overview' | 'assessments' | 'policy' | 'documents';

function statusKind(status: string | undefined): string {
  if (status === 'Assessed' || status === 'Completed') return 'pos';
  if (status === 'Assessing' || status === 'Running') return 'info';
  if (status === 'Failed') return 'neg';
  return 'neutral';
}

/** The sentence or two the Locate step read from the documents about what is proposed, if the case has been located. */
function derivedSummary(siteLocation: string | null | undefined): string | undefined {
  if (!siteLocation) return undefined;
  try {
    const v = (JSON.parse(siteLocation) as { summary?: unknown }).summary;
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

function isBlocked(doc: PlanningDocument): boolean {
  return doc.scanStatus === 'Infected' || doc.scanStatus === 'Failed' || doc.scanStatus === 'Pending';
}

function blockedReason(doc: PlanningDocument): string {
  if (doc.scanStatus === 'Pending') return 'This file is still being scanned for malware and cannot be opened yet.';
  const base = doc.scanStatus === 'Infected'
    ? 'Malware was detected in this file, so it was removed and cannot be opened.'
    : 'This file failed the malware scan and was rejected, so it cannot be opened.';
  return doc.scanDetail ? `${base} ${doc.scanDetail}` : base;
}

function scanBadge(doc: PlanningDocument) {
  if (doc.scanStatus === 'Infected') return <> <Badge kind="neg">Malware removed</Badge></>;
  if (doc.scanStatus === 'Failed') return <> <Badge kind="neg">Rejected</Badge></>;
  if (doc.scanStatus === 'Pending') return <> <Badge kind="info">Scanning…</Badge></>;
  return null;
}

function scoreKind(score: number | undefined): string {
  if (score == null) return 'neutral';
  if (score < 40) return 'pos';
  if (score < 64) return 'warn';
  return 'neg';
}

function RiskBadge({ score, band }: { score?: number; band?: string }) {
  if (score == null) return <span style={{ color: 'var(--ink-3)' }}>—</span>;
  return <Badge kind={scoreKind(score)}>{band ?? 'Scored'} · {score}</Badge>;
}

// List <-> detail navigation goes through window.location.hash so the browser Back button works.
export function CasesPage({ id }: { id?: string }) {
  const { can, loading: permLoading } = usePlanningPermissions();
  const canWrite = can('planning-cases:write');
  // Re-running Locate spends model and map-service calls and overwrites the confirmed location, so it is offered to administrators only.
  const isAdmin = can('planning-settings:manage');
  const canRelocate = LOCATE_ENABLED() && isAdmin;

  if (!permLoading && !can('planning-cases:read')) {
    return (
      <Shell current="cases" crumbs={[{ label: 'Planning' }, { label: 'Cases' }]}>
        <div className="card">
          <div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            You don't have permission to view this page.
          </div>
        </div>
      </Shell>
    );
  }

  if (id) {
    return <CaseDetail id={id} canAssess={can('planning-assessments:write')} canWrite={canWrite} canRelocate={canRelocate} canReassess={isAdmin} />;
  }
  return <CaseList onOpen={openId => { window.location.hash = `#/cases/${openId}`; }} />;
}

function CaseList({ onOpen }: { onOpen: (id: string) => void }) {
  const [items, setItems] = useState<PlanningCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    listPlanningCases({ scope: 'all' })
      .then(p => { if (!cancelled) { setItems(p.items); setNextToken(p.nextToken); } })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function loadMore() {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true); setMoreError(null);
    try {
      const p = await listPlanningCases({ nextToken, scope: 'all' });
      setItems(prev => [...prev, ...p.items.filter(c => !prev.some(x => x.id === c.id))]);
      setNextToken(p.nextToken);
    } catch (e) {
      setMoreError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  const sentinel = useInfiniteScroll(() => void loadMore(), !!nextToken && !loadingMore && !moreError && !loading, items.length);

  return (
    <Shell current="cases" crumbs={[{ label: 'Planning' }, { label: 'Cases' }]}>
      <PageHeader
        title="Cases"
        subtitle="Each case is one planning matter to assess: upload its planning statement, choose the policy inputs it is judged against, and request a risk assessment."
      />

      <div className="card">
        <div className="card-body flush">
          {error ? <div className="state-fill error">Failed to load cases: {error}</div> : (
            <table className="tbl tbl-list">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="hide-mobile">Reference</th>
                  <th className="hide-mobile">Jurisdiction</th>
                  <th>Status</th>
                  <th>Risk</th>
                  <th className="hide-mobile">Last assessed</th>
                </tr>
              </thead>
              <tbody>
                {loading ? Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} style={{ '--skel-delay': `${i * 60}ms` } as React.CSSProperties}>
                    <td><span className="skel" style={{ width: 160 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 90 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 110 }} /></td>
                    <td><span className="skel" style={{ width: 70 }} /></td>
                    <td><span className="skel" style={{ width: 90 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 100 }} /></td>
                  </tr>
                )) : items.map(c => (
                  <tr key={c.id} className="clickable" onClick={() => onOpen(c.id)}>
                    <td className="primary">{c.name}{c.caseType === 'PreApplication' && <> <Badge kind="neutral">Pre-app</Badge></>}</td>
                    <td className="hide-mobile">{c.reference || c.id}</td>
                    <td className="hide-mobile">{c.jurisdiction || '—'}</td>
                    <td>
                      <Badge kind={statusKind(c.assessmentStatus)}>{c.assessmentStatus ?? 'NotAssessed'}</Badge>
                      {c.deletedAt && <> <Badge kind="neg">Deleted by user</Badge></>}
                    </td>
                    <td><RiskBadge score={c.latestScore} band={c.latestRiskBand} /></td>
                    <td className="mono hide-mobile" style={{ fontSize: 12 }}>{c.lastAssessedAt ? fmtDateTime(c.lastAssessedAt) : '—'}</td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>No cases yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
          {!error && !loading && (nextToken || moreError) && (
            <div ref={sentinel} style={{ padding: 12, textAlign: 'center', fontSize: 12.5, color: 'var(--ink-3)' }}>
              {moreError ? (
                <>
                  <div style={{ color: 'var(--neg)', marginBottom: 8 }}>Failed to load more cases: {moreError}</div>
                  <button className="btn" onClick={() => void loadMore()}>Try again</button>
                </>
              ) : loadingMore ? 'Loading…' : ''}
            </div>
          )}
        </div>
      </div>

    </Shell>
  );
}

function CaseDetail({ id, canAssess, canWrite, canRelocate, canReassess }: { id: string; canAssess: boolean; canWrite: boolean; canRelocate: boolean; canReassess: boolean }) {
  const { toasts, addToast, removeToast } = useToasts();

  const [item, setItem] = useState<PlanningCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [tab, setTab] = useState<DetailTab>('overview');

  const [allPolicyInputs, setAllPolicyInputs] = useState<PolicyInput[]>([]);
  const [identities, setIdentities] = useState<Map<string, UserIdentity>>(new Map());
  const [identitiesLoading, setIdentitiesLoading] = useState(true);
  const assessedBy = [...(item?.assessments ?? [])]
    .filter(a => a.assessedAt && a.ownerUserId)
    .sort((a, b) => (b.assessedAt ?? '').localeCompare(a.assessedAt ?? ''))[0]?.ownerUserId ?? undefined;
  const uploadedBy = item?.ownerUserId ?? undefined;
  const identityKey = [uploadedBy, assessedBy].filter((v): v is string => !!v).sort().join(',');
  const linkedKey = (item?.policyInputs ?? []).map(p => p.id).sort().join(',');
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const [docBusy, setDocBusy] = useState<string | null>(null);

  const [assessment, setAssessment] = useState<PlanningAssessment | null>(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!identityKey) { setIdentities(new Map()); setIdentitiesLoading(loading); return () => { cancelled = true; }; }
    setIdentitiesLoading(true);
    resolveUserIdentities(identityKey.split(','))
      .then(m => { if (!cancelled) setIdentities(m); })
      .finally(() => { if (!cancelled) setIdentitiesLoading(false); });
    return () => { cancelled = true; };
  }, [identityKey, loading]);

  const [relocating, setRelocating] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function deleteForGood() {
    if (!item) return;
    setDeleting(true); setDeleteError(null);
    try {
      await runEvent(connId => emitDeletePlanningCase(item.id, getUserEmail() ?? undefined, connId), 30_000);
      window.location.hash = '#/cases';
    } catch (e) {
      setDeleteError(errMessage(e, 'Failed to delete the case'));
      setDeleting(false);
    }
  }

  const reload = () => setReloadTick(t => t + 1);
  const reloadSettled = useSettledReload(reload);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getPlanningCase(id)
      .then(d => {
        if (cancelled) return;
        if (!d) throw new Error('Case not found');
        setItem(d);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, reloadTick]);

  useEffect(() => {
    let cancelled = false;
    setPolicyLoading(true); setPolicyError(null);
    if (!linkedKey) { setAllPolicyInputs([]); setPolicyLoading(loading); return () => { cancelled = true; }; }
    Promise.all(linkedKey.split(',').map(pid => getPolicyInput(pid)))
      .then(list => { if (!cancelled) setAllPolicyInputs(list.filter((p): p is PolicyInput => !!p).sort((a, b) => a.name.localeCompare(b.name))); })
      .catch((e: Error) => { if (!cancelled) setPolicyError(e.message); })
      .finally(() => { if (!cancelled) setPolicyLoading(false); });
    return () => { cancelled = true; };
  }, [linkedKey, loading]);

  // No push arrives when the assessment workflow finishes, so poll lightly while one is running.
  useEffect(() => {
    if (item?.assessmentStatus !== 'Assessing') return;
    const timer = setInterval(reload, 10_000);
    return () => clearInterval(timer);
  }, [item?.assessmentStatus]);

  const anyPendingScan = (item?.documents ?? []).some(d => d.scanStatus === 'Pending');
  useEffect(() => {
    if (!anyPendingScan) return;
    const timer = setInterval(reload, 5_000);
    return () => clearInterval(timer);
  }, [anyPendingScan]);

  const costs = summariseCosts(parseUsageLines(item?.usageLines), item?.osMapTiles, item?.osMapCostGbp, item?.osMapCachedTiles, item?.osMapCachedCostGbp, item?.tokensSpent, item?.tokensRefunded);
  const locateUsage = parseLocateUsage(item?.siteLocation);
  const assessRuns = (item?.assessments ?? []).filter(a => a.inputTokens != null || a.estimatedCostUsd != null);
  const assessUsage = {
    runs: assessRuns.length,
    inputTokens: assessRuns.reduce((n, a) => n + (a.inputTokens ?? 0), 0),
    outputTokens: assessRuns.reduce((n, a) => n + (a.outputTokens ?? 0), 0),
    estimatedCostUsd: assessRuns.reduce((n, a) => n + (a.estimatedCostUsd ?? 0), 0),
  };
  const totalCostUsd = (locateUsage?.estimatedCostUsd ?? 0) + assessUsage.estimatedCostUsd;
  const sortedAssessments = [...(item?.assessments ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const shownAssessmentId = sortedAssessments[0]?.id ?? null;

  useEffect(() => {
    if (!shownAssessmentId) { setAssessment(null); return; }
    let cancelled = false;
    setAssessmentLoading(true); setAssessmentError(null);
    getPlanningAssessment(shownAssessmentId)
      .then(a => { if (!cancelled) setAssessment(a ?? null); })
      .catch((e: Error) => { if (!cancelled) setAssessmentError(e.message); })
      .finally(() => { if (!cancelled) setAssessmentLoading(false); });
    return () => { cancelled = true; };
  }, [shownAssessmentId, item?.assessmentStatus]);

  async function openPolicyDocument(policyInputId: string, s3Key: string) {
    setDocBusy(s3Key);
    try {
      window.open(await getPolicyInputDownloadUrl(policyInputId, s3Key), '_blank', 'noopener');
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to open document'));
    } finally {
      setDocBusy(null);
    }
  }

  async function openDocument(doc: PlanningDocument) {
    setDocBusy(doc.id);
    try {
      window.open(await getPlanningDocumentDownloadUrl(doc.id), '_blank', 'noopener');
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to open document'));
    } finally {
      setDocBusy(null);
    }
  }

  async function requestAssessment() {
    setRequesting(true);
    try {
      await runEvent(connId => emitRequestPlanningAssessment(id, getUserEmail() ?? undefined, connId));
      addToast('info', 'Assessment requested — this can take a few minutes.');
      reloadSettled();
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to request assessment'));
    } finally {
      setRequesting(false);
    }
  }

  async function downloadPdf() {
    if (!assessment?.pdf) return;
    setPdfBusy(true);
    try {
      window.open(await getPlanningDocumentDownloadUrl(assessment.pdf.id), '_blank', 'noopener');
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to open PDF'));
    } finally {
      setPdfBusy(false);
    }
  }

  const val = (v: string | undefined) => loading ? <span className="skel" style={{ width: 120 }} /> : (v || '—');
  const person = (userId: string | undefined) => {
    if (loading || (userId && identitiesLoading)) return <span className="skel" style={{ width: 120 }} />;
    if (!userId) return '—';
    const who = identities.get(userId);
    if (!who) return '—';
    if (who.name && who.email) return <>{who.name} <span style={{ color: 'var(--ink-3)' }}>({who.email})</span></>;
    return who.name || who.email;
  };
  const linkedIds = new Set((item?.policyInputs ?? []).map(p => p.id));
  const docs = item?.documents ?? [];
  const tabs: Array<{ id: DetailTab; label: string; count?: number }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'assessments', label: 'Assessments', count: item?.assessments.length ? item.assessments.length : undefined },
    { id: 'policy', label: 'Policy inputs', count: linkedIds.size > 0 ? linkedIds.size : undefined },
    { id: 'documents', label: 'Documents', count: docs.length > 0 ? docs.length : undefined },
  ];
  const hasStatement = docs.some(d => d.category === STATEMENT_CATEGORY);
  const alreadyAssessed = item?.assessmentStatus === 'Assessed' || item?.assessmentStatus === 'Completed';
  const canRequest = canAssess && !loading && item?.assessmentStatus !== 'Assessing' && (!alreadyAssessed || canReassess) && hasStatement && linkedIds.size > 0;
  const requestHint = !hasStatement ? 'Attach a planning statement first'
    : linkedIds.size === 0 ? 'Link at least one policy input first'
    : item?.assessmentStatus === 'Assessing' ? 'An assessment is already running'
    : alreadyAssessed && !canReassess ? 'This case has already been assessed' : undefined;

  const analysisCard = (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-hd">
        Analysis
        {assessment?.pdf && (
          <div className="card-hd-actions">
            <button className="btn" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => void downloadPdf()} disabled={pdfBusy}>
              {pdfBusy ? 'Opening…' : 'Open PDF'}
            </button>
          </div>
        )}
      </div>
      <div className="card-body">
        {assessmentError ? (
          <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>Failed to load assessment: {assessmentError}</div>
        ) : assessmentLoading || (loading && !item) ? (
          <span className="skel" style={{ width: '100%', height: 120 }} />
        ) : !shownAssessmentId ? (
          <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No assessment has been run for this case yet.</span>
        ) : assessment ? (
          <>
            {assessment.status === 'Failed' && (
              <div style={{ color: 'var(--neg)', fontSize: 12.5, marginBottom: 10 }}>{assessment.error ?? 'This assessment failed.'}</div>
            )}
            {assessment.analysis
              ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(assessment.analysis) }} />
              : <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>{assessment.status === 'Running' ? 'The assessment is still running.' : 'No analysis text.'}</span>}
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line-2)' }}>
              {fmtTokens(assessment.inputTokens)} tokens in / {fmtTokens(assessment.outputTokens)} out — est. {fmtCost(assessment.estimatedCostUsd)}
              {assessment.assessedAt ? ` — assessed ${fmtDateTime(assessment.assessedAt)}` : ''}
              {assessment.truncated || assessment.finalTextTruncated ? ' — output was truncated' : ''}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );

  if (relocating && canRelocate) {
    // A site confirmed as just a pin, before a Location Plan was added, is searched for again so the plan's outline is
    // read; otherwise relocating starts from what was confirmed.
    const saved = parseSavedLocation(item?.siteLocation);
    const hasLocationPlan = !!item?.documents.some(d => d.category === LOCATION_PLAN_CATEGORY);
    const startFrom = saved && (saved.boundary || !hasLocationPlan) ? item?.siteLocation : null;
    return (
      <Shell
        current="cases"
        crumbs={[{ label: 'Planning' }, { label: 'Cases', href: '#/cases' }, { label: item?.name ?? 'Case' }, { label: 'Relocate' }]}
      >
        <PageHeader
          title="Relocate site"
          actions={<button className="btn" onClick={() => setRelocating(false)}>Cancel</button>}
        />
        <LocateStep
          caseId={id}
          preApplication={item?.caseType === 'PreApplication'}
          saved={item?.caseType === 'PreApplication' ? item.siteLocation : startFrom}
          onDone={() => { setRelocating(false); addToast('success', 'Site location updated'); reloadSettled(); }}
        />
        <Toasts items={toasts} onRemove={removeToast} />
      </Shell>
    );
  }

  return (
    <Shell
      current="cases"
      crumbs={[{ label: 'Planning' }, { label: 'Cases', href: '#/cases' }, { label: item?.name ?? 'Case' }]}
    >
      <PageHeader
        title={loading && !item ? '…' : (item?.name ?? 'Case')}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {item && <Badge kind={statusKind(item.assessmentStatus)}>{item.assessmentStatus ?? 'NotAssessed'}</Badge>}
            {item?.deletedAt && <Badge kind="neg">Deleted by user</Badge>}
            {item && canRelocate && (
              <button className="btn" onClick={() => setRelocating(true)} title="Run Locate again and replace the saved site location">
                Relocate
              </button>
            )}
            {item && canWrite && (
              <button className="btn btn-danger" onClick={() => { setDeleteError(null); setConfirmingDelete(true); }} disabled={deleting}>
                Delete case
              </button>
            )}
            {item && canAssess && (
              <button
                className="btn btn-primary" onClick={() => void requestAssessment()}
                disabled={!canRequest || requesting} title={requestHint}
              >
                {requesting || item.assessmentStatus === 'Assessing' ? 'Assessing…' : alreadyAssessed ? 'Reassess' : 'Request assessment'}
              </button>
            )}
          </div>
        }
      />

      {error && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--neg)', fontSize: 12.5 }}>
          Failed to load case: {error}
        </div>
      )}

      {!error && (
        <>
          <CaseCostKpis costs={item ? costs : null} loading={loading && !item} />
          <div className="tabs">
            {tabs.map(({ id: tabId, label, count }) => (
              <button key={tabId} className={`tab-btn${tab === tabId ? ' active' : ''}`} onClick={() => setTab(tabId)}>
                {label}
                {count != null && <span className="tab-count">{count}</span>}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <>
              <div className="card">
                <div className="card-hd">Details</div>
                <div className="card-body">
                  <dl className="dl">
                    <div className="dl-row"><div className="dl-label">Type</div><div className="dl-val">{loading ? <span className="skel" /> : item?.caseType === 'PreApplication' ? 'Pre-application check (assessed from the description)' : 'Application'}</div></div>
                    <div className="dl-row"><div className="dl-label">Reference</div><div className="dl-val">{val(item?.reference || item?.id)}</div></div>
                    <div className="dl-row"><div className="dl-label">Jurisdiction</div><div className="dl-val">{val(item?.jurisdiction)}</div></div>
                    <div className="dl-row"><div className="dl-label">Description</div><div className="dl-val">{val(item?.description || derivedSummary(item?.siteLocation))}{!loading && !item?.description && derivedSummary(item?.siteLocation) && <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11.5 }}>Summarised from the documents when the site was located.</span>}</div></div>
                    <div className="dl-row">
                      <div className="dl-label">Latest risk</div>
                      <div className="dl-val">{loading ? <span className="skel" /> : <RiskBadge score={item?.latestScore} band={item?.latestRiskBand} />}</div>
                    </div>
                    <div className="dl-row"><div className="dl-label">Last assessed</div><div className="dl-val">{val(item?.lastAssessedAt ? fmtDateTime(item.lastAssessedAt) : undefined)}</div></div>
                    <div className="dl-row"><div className="dl-label">Uploaded by</div><div className="dl-val">{person(uploadedBy)}</div></div>
                    <div className="dl-row"><div className="dl-label">Assessed by</div><div className="dl-val">{person(assessedBy)}</div></div>
                    <div className="dl-row"><div className="dl-label">Created</div><div className="dl-val">{val(item ? fmtDateTime(item.createdAt) : undefined)}</div></div>
                    <div className="dl-row"><div className="dl-label">Updated</div><div className="dl-val">{val(item ? fmtDateTime(item.updatedAt) : undefined)}</div></div>
                  </dl>
                  {item?.assessmentStatus === 'Failed' && item.assessmentError && (
                    <div style={{ color: 'var(--neg)', fontSize: 12.5, marginTop: 12 }}>Last assessment failed: {item.assessmentError}</div>
                  )}
                </div>
              </div>
              {parseSiteMap(item?.siteLocation) && (
                <div className="card">
                  <div className="card-hd">Site location</div>
                  <div className="card-body"><SiteMapPanel siteLocation={item?.siteLocation} caseId={item?.id} /></div>
                </div>
              )}
              {item?.siteLocation && (
                <div className="card">
                  <div className="card-hd">How the site was located</div>
                  <div className="card-body lm-readonly"><SiteLocationNotes siteLocation={item.siteLocation} explainMissingOutline /></div>
                </div>
              )}
              <SiteFeaturesCard siteLocation={item?.siteLocation} loading={loading && !item} />
              <AssessInputCard siteLocation={item?.siteLocation} loading={loading && !item} />
              {!costs.empty ? (
                <div className="card">
                  <div className="card-hd">
                    Costs
                    <span className="card-hd-sub">Every model call priced by the model it ran on; OS map tiles at £0.0331 per 15, before the free allowance. Mock runs cost nothing.</span>
                  </div>
                  <div className="card-body flush"><CaseCostBreakdown costs={costs} /></div>
                </div>
              ) : (
              <div className="card">
                <div className="card-hd">
                  Usage and cost by phase
                  <span className="card-hd-sub">Recorded before cost metering, at a flat Sonnet rate. Mock runs are not charged.</span>
                </div>
                <div className="card-body flush">
                  <table className="tbl tbl-list">
                    <thead>
                      <tr>
                        <th>Phase</th>
                        <th className="num hide-mobile">Tokens in</th>
                        <th className="num hide-mobile">Tokens out</th>
                        <th className="num">Est. cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && !item ? (
                        <tr><td><span className="skel" /></td><td className="hide-mobile"><span className="skel" /></td><td className="hide-mobile"><span className="skel" /></td><td><span className="skel" /></td></tr>
                      ) : (
                        <>
                          <tr>
                            <td>Locate{locateUsage ? "" : <span style={{ color: "var(--ink-3)" }}> (not recorded)</span>}</td>
                            <td className="num mono hide-mobile">{locateUsage ? fmtTokens(locateUsage.inputTokens) : "—"}</td>
                            <td className="num mono hide-mobile">{locateUsage ? fmtTokens(locateUsage.outputTokens) : "—"}</td>
                            <td className="num mono">{locateUsage ? fmtCost(locateUsage.estimatedCostUsd) : "—"}</td>
                          </tr>
                          <tr>
                            <td>Assess{assessUsage.runs > 1 && <span style={{ color: "var(--ink-3)" }}> ({assessUsage.runs} runs)</span>}{assessUsage.runs === 0 && <span style={{ color: "var(--ink-3)" }}> (not run)</span>}</td>
                            <td className="num mono hide-mobile">{assessUsage.runs ? fmtTokens(assessUsage.inputTokens) : "—"}</td>
                            <td className="num mono hide-mobile">{assessUsage.runs ? fmtTokens(assessUsage.outputTokens) : "—"}</td>
                            <td className="num mono">{assessUsage.runs ? fmtCost(assessUsage.estimatedCostUsd) : "—"}</td>
                          </tr>
                          {(locateUsage?.osMapTiles ?? 0) > 0 && (
                            <tr>
                              <td>OS map <span style={{ color: "var(--ink-3)" }}>({locateUsage!.osMapTiles.toLocaleString()} premium tiles, before the free allowance)</span></td>
                              <td className="num mono hide-mobile">—</td>
                              <td className="num mono hide-mobile">—</td>
                              <td className="num mono">£{locateUsage!.osMapCostGbp.toFixed(2)}</td>
                            </tr>
                          )}
                          <tr style={{ fontWeight: 600 }}>
                            <td>Total</td>
                            <td className="num mono hide-mobile">{fmtTokens((locateUsage?.inputTokens ?? 0) + assessUsage.inputTokens)}</td>
                            <td className="num mono hide-mobile">{fmtTokens((locateUsage?.outputTokens ?? 0) + assessUsage.outputTokens)}</td>
                            <td className="num mono">{fmtCost(totalCostUsd)}{(locateUsage?.osMapCostGbp ?? 0) > 0 ? ` + £${locateUsage!.osMapCostGbp.toFixed(2)}` : ''}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
              {analysisCard}
            </>
          )}

          {tab === 'assessments' && (
            loading && !item
              ? <div className="card"><div className="card-body"><span className="skel" style={{ width: 160 }} /></div></div>
              : <AssessmentHistory runs={item?.assessments ?? []} preApplication={item?.caseType === 'PreApplication'} />
          )}

          {tab === 'policy' && (
            policyError ? (
              <div className="card"><div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--neg)', fontSize: 12.5 }}>Failed to load policy inputs: {policyError}</div></div>
            ) : policyLoading || (loading && !item) ? (
              <div className="card"><div className="card-body"><span className="skel" style={{ width: 160 }} /></div></div>
            ) : allPolicyInputs.length === 0 ? (
              <div className="card"><div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12.5 }}>No policy inputs are linked to this case.</div></div>
            ) : allPolicyInputs.map(p => (
              <div className="card" key={p.id}>
                <div className="card-hd">
                  <a href={`#/policy-inputs/${p.id}`}>{p.name}</a>
                  {p.scope === 'Global' && <span style={{ marginLeft: 8 }}><Badge kind="info">Shared</Badge></span>}
                  <span className="card-hd-sub">{p.jurisdiction || 'No jurisdiction'} · output {p.outputStatus}{p.outputGeneratedAt ? ` ${fmtDateTime(p.outputGeneratedAt)}` : ''}</span>
                </div>
                <div className="card-body">
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>Source documents</div>
                  {p.documents.length === 0 ? (
                    <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No documents.</div>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                      {p.documents.map(d => {
                        const blocked = !!d.scanStatus && d.scanStatus !== 'Clean';
                        const why = d.scanStatus === 'Pending'
                          ? 'This file is still being scanned for malware and cannot be opened yet.'
                          : `This file failed the malware scan and cannot be opened.${d.scanDetail ? ` ${d.scanDetail}` : ''}`;
                        return (
                          <li key={d.s3Key}>
                            {blocked ? (
                              <span className="doc-unavailable">{d.filename}<span className="doc-unavailable-why">{why}</span></span>
                            ) : (
                              <a href="#" className="doc-link" title="Open document in a new tab"
                                onClick={e => { e.preventDefault(); if (docBusy !== d.s3Key) void openPolicyDocument(p.id, d.s3Key); }}>
                                {I.doc}{d.filename}<span className="doc-link-ext">{I.ext}</span>
                              </a>
                            )}{' '}
                            <span style={{ color: 'var(--ink-3)' }}>({Math.max(1, Math.round(d.size / 1024)).toLocaleString('en-GB')} KB)</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {p.output || p.supplementaryNotes ? (
                    <details style={{ margin: '16px 0 0' }}>
                      <summary style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', cursor: 'pointer' }}>Text given to the assessment</summary>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', margin: '12px 0 6px' }}>Output</div>
                      {p.output
                        ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(p.output) }} />
                        : <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No output generated yet.</div>}
                      {p.supplementaryNotes && (
                        <>
                          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', margin: '16px 0 6px' }}>Supplementary notes</div>
                          <div dangerouslySetInnerHTML={{ __html: renderMarkdown(p.supplementaryNotes) }} />
                        </>
                      )}
                    </details>
                  ) : (
                    <>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', margin: '16px 0 6px' }}>Text given to the assessment</div>
                      <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No output generated yet.</div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}

          {tab === 'documents' && (
            <div className="card">
              <div className="card-hd">
                Documents
                {docs.length > 0 && <span className="card-hd-sub">{docs.length}</span>}
              </div>
              <div className="card-body flush">
                <table className="tbl tbl-list">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th className="hide-mobile">Category</th>
                      <th className="hide-mobile">Size</th>
                      <th className="hide-mobile">Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !item ? (
                      <tr><td><span className="skel" style={{ width: 180 }} /></td><td className="hide-mobile"><span className="skel" /></td><td className="hide-mobile"><span className="skel" /></td><td className="hide-mobile"><span className="skel" /></td></tr>
                    ) : docs.map(doc => (
                      <tr key={doc.id} className={isBlocked(doc) ? 'row-disabled' : 'clickable'}
                        onClick={isBlocked(doc) ? undefined : () => { if (docBusy !== doc.id) void openDocument(doc); }}>
                        <td className="primary">
                          {isBlocked(doc) ? (
                            <span className="doc-unavailable">
                              {doc.filename}
                              {scanBadge(doc)}
                              <span className="doc-unavailable-why">{blockedReason(doc)}</span>
                            </span>
                          ) : (
                            <a href="#" className="doc-link" title="Open document in a new tab" aria-busy={docBusy === doc.id}
                              onClick={e => { e.preventDefault(); e.stopPropagation(); if (docBusy !== doc.id) void openDocument(doc); }}>
                              {I.doc}{doc.filename}<span className="doc-link-ext">{I.ext}</span>
                            </a>
                          )}
                          {!isBlocked(doc) && scanBadge(doc)}
                        </td>
                        <td className="hide-mobile">{doc.category || '—'}</td>
                        <td className="mono hide-mobile" style={{ fontSize: 12 }}>{doc.size != null ? `${(doc.size / 1024).toFixed(1)} KB` : '—'}</td>
                        <td className="mono hide-mobile" style={{ fontSize: 12 }}>{fmtDateTime(doc.createdAt)}</td>
                      </tr>
                    ))}
                    {docs.length === 0 && !loading && (
                      <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>No documents attached yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </>
      )}

      {confirmingDelete && item && (
        <ConfirmDialog title="Permanently delete this case?" confirmLabel="Delete permanently" busyLabel="Deleting…" busy={deleting} error={deleteError}
          onConfirm={() => void deleteForGood()} onCancel={() => setConfirmingDelete(false)}>
          <p><strong>{item.name}</strong> will be deleted for good and cannot be recovered.</p>
          {item.deletedAt
            ? <p>The owner already removed it from their list. This removes it from the system.</p>
            : <p>The owner has not deleted this case; it is still in their list and will disappear from it.</p>}
          <p>Its links to documents, policy inputs and assessments are removed with it.</p>
        </ConfirmDialog>
      )}
      <Toasts items={toasts} onRemove={removeToast} />
    </Shell>
  );
}
